import React from 'react';
import { Layout, Row, Col, Card, Button, Modal, message } from 'antd';
import { EditFilled, UploadOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import WalkthroughHeatMapRangesField from '../walkthrough_heat_map_ranges/WalkthroughHeatMapRangesField';
import WalkthroughHeatMapLegend from '../walkthrough_heat_map_ranges/WalkthroughHeatMapLegend';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import ScrollToTopOnMount from '../scroll_to_top_on_mount/ScrollToTopOnMount';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	siteSurveyTemplatesRestCrudThunksForBuyer,
	siteSurveyTemplatesRestCrudThunksForSupplier,
} from '../../thunks/site_survey_templates_thunks';
import {
	taskListsRestCrudThunksForBuyer,
	taskListsRestCrudThunksForSupplier,
} from '../../thunks/task_lists_thunks';
import TaskListRO from '../task_list/TaskListRO';
import { ExportToCsv } from 'export-to-csv';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import OWImporter from '../ow_import_grid/OWImporter';
import OWImportButton from '../ow_import_grid/OWImportButton';
import { renderAnswerOptionsCell } from '../ow_import_grid/AnswerOptionsField';
import { renderFollowUpActionsCell } from '../ow_import_grid/FollowUpActionsField';
import {
	getAnswerOptionsForBE,
	getAnswerOptionsForExport,
	getAnswerOptionsSourceValue,
	getFollowUpActionsForBE,
	getFollowUpActionsForExport,
	getSiteSurveyAnswerTypeDisplayName,
	isTaskListEnabled,
	siteSurveyTaskRecordHook,
	SITE_SURVEY_ANSWER_TYPE,
	validateForDuplicateTitlesUnderSameGroup,
} from '../../utils/SiteSurveyUtils';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

const { Content } = Layout;

require('./SiteSurveyTemplatesDetailDetailsPage.less');

const IMPORT_FIELDS = [
	{
		label: 'Title',
		key: 'title',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Description',
		key: 'description',
		type: 'string',
	},
	{
		label: 'Group Title',
		key: 'groupTitle',
		type: 'string',
	},
	{
		label: 'Answer Type',
		key: 'questionType',
		type: 'enum',
		config: {
			options: [
				{ label: 'Multiple Choice', value: 'multipleChoice' },
				{ label: 'Free Text', value: 'textbox' },
				...(isTaskListEnabled() ? [{ label: 'Capture Tasks', value: 'taskList' }] : []),
				{ label: 'Checklist', value: SITE_SURVEY_ANSWER_TYPE.CHECKLIST },
			],
		},
		constraints: [{ type: 'required' }],
	},
	{
		// One merged column for every answer type. The editor (and the CSV value
		// format) is chosen by the row's answer type; the value is split into the
		// per-type backing keys only when the task list is submitted.
		label: 'Answer Options',
		key: 'answerOptions',
		type: 'string',
		renderCustomCell: renderAnswerOptionsCell,
	},
	{
		// Task-level follow-up actions — applies to every answer type, independent
		// of the per-choice follow-ups configured inside Answer Options.
		label: 'Follow-up actions',
		key: 'followUpActions',
		type: 'string',
		renderCustomCell: renderFollowUpActionsCell,
	},
];

const csvOptions = {
	fieldSeparator: ',',
	quoteStrings: '"',
	decimalSeparator: '.',
	showLabels: true,
	showTitle: false,
	title: 'Task List',
	useTextFile: false,
	useBom: true,
	headers: [
		'Title',
		'Description',
		'Group Title',
		'Answer Type',
		'Answer Options',
		'Follow-up actions',
	],
};

class SiteSurveyTemplatesDetailDetailsPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
		this.state = {
			initialLoading: true,
			addTaskModalVisible: false,
			editTaskListVisible: false,
			heatMapRangesModalVisible: false,
			heatMapRangesDraft: undefined,
			savingHeatMapRanges: false,
		};
	}

	componentDidMount() {
		const {
			getSiteSurveyTemplate,

			match,
		} = this.props;
		const siteSurveyTemplateId = match.params.id;
		getSiteSurveyTemplate(siteSurveyTemplateId).finally(() => {
			this.setState({ initialLoading: false });
		});
	}

	showEditTaskList = () => this.setState({ editTaskListVisible: true });

	_onEditTaskListComplete = () => this.setState({ editTaskListVisible: false });

	getSourceDataForSiteSurveyTemplate = (sst) => {
		const tasks = nullSafeGetOrElse('taskList.tasks', sst, []);
		return tasks.map((task) => ({
			...task,
			answerOptions: getAnswerOptionsSourceValue(task),
		}));
	};

	onDownloadTaskList = () => {
		const { siteSurveyTemplate } = this.props;
		const tasks = nullSafeGetOrElse('taskList.tasks', siteSurveyTemplate, []);
		const formattedTasks = tasks.map((task) => ({
			title: task.title,
			description: task.description || '',
			group: task.groupTitle || '',
			questionType: getSiteSurveyAnswerTypeDisplayName(task.questionType),
			answerOptions: getAnswerOptionsForExport(task),
			followUpActions: getFollowUpActionsForExport(task),
		}));
		const csvExporter = new ExportToCsv({ ...csvOptions, filename: siteSurveyTemplate.title });
		csvExporter.generateCsv(formattedTasks);
	};

	onDataLoad = (results) => {
		const { match, siteSurveyTemplate, getSiteSurveyTemplate, updateTaskList } = this.props;
		const siteSurveyTemplateId = match.params.id;
		return new Promise((resolve, reject) => {
			const newTasks = results.validData.map((task) => {
				const {
					answerOptions,
					followUpActions,
					multipleChoicesWithLabel,
					checklistTasks,
					...rest
				} = task;
				return {
					...rest,
					...getAnswerOptionsForBE(task.questionType, answerOptions),
					...getFollowUpActionsForBE(followUpActions),
				};
			});
			const newTaskList = { ...siteSurveyTemplate.taskList, tasks: newTasks };
			updateTaskList(newTaskList)
				.then(() => {
					resolve('Task Lists updated sucessfully');
					getSiteSurveyTemplate(siteSurveyTemplateId);
				})
				.catch((err) => reject(err));
		});
	};

	recordHook = siteSurveyTaskRecordHook;

	getSavedHeatMapRanges = () =>
		nullSafeGetOrElse('walkthroughHeatMapRanges', this.props.siteSurveyTemplate, null);

	// `undefined` means "not edited in this modal session", so the saved value is read straight off
	// the template rather than mirrored into state — nothing to sync when the template refetches.
	getHeatMapRanges = () =>
		this.state.heatMapRangesDraft === undefined
			? this.getSavedHeatMapRanges()
			: this.state.heatMapRangesDraft;

	onChangeHeatMapRanges = (heatMapRangesDraft) => this.setState({ heatMapRangesDraft });

	showHeatMapRangesModal = () =>
		this.setState({ heatMapRangesModalVisible: true, heatMapRangesDraft: undefined });

	// Dropping the draft on cancel is what makes the modal discard edits rather than leak them back
	// into the read-only legend behind it.
	hideHeatMapRangesModal = () =>
		this.setState({ heatMapRangesModalVisible: false, heatMapRangesDraft: undefined });

	onSaveHeatMapRanges = () => {
		const { siteSurveyTemplate, updateSiteSurveyTemplate, getSiteSurveyTemplate, match } =
			this.props;
		this.setState({ savingHeatMapRanges: true });
		updateSiteSurveyTemplate({
			...siteSurveyTemplate,
			walkthroughHeatMapRanges: this.getHeatMapRanges(),
		})
			.then(() => {
				message.success('Walkthrough scores updated!');
				this.setState({ heatMapRangesModalVisible: false, heatMapRangesDraft: undefined });
				return getSiteSurveyTemplate(match.params.id);
			})
			.catch(message.error)
			.finally(() => this.setState({ savingHeatMapRanges: false }));
	};

	render() {
		const { siteSurveyTemplate, userType, siteSurveyTemplatesFetching } = this.props;

		return this.state.initalLoading || siteSurveyTemplatesFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="siteSurveyTemplatesDetailDetailsPage">
				<ScrollToTopOnMount />
				<Row gutter={16}>
					{siteSurveyTemplate.description ? (
						<Col span={24} className="rowSpacing">
							<Card>
								<div className="siteSurveyTemplatesDetailDetailsPage__descSection">
									<div className="siteSurveyTemplatesDetailDetailsPage__descSection">
										{siteSurveyTemplate.description}
									</div>
								</div>
							</Card>
						</Col>
					) : null}
					<Col span={24} className="rowSpacing">
						<Card
							title="Walkthrough Scores"
							extra={
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES}>
									<Button onClick={this.showHeatMapRangesModal}>
										<EditFilled /> Edit
									</Button>
								</AccessPermissionChecker>
							}
						>
							<WalkthroughHeatMapLegend ranges={this.getSavedHeatMapRanges()} />
						</Card>
					</Col>
					{this.state.heatMapRangesModalVisible ? (
						<Modal
							visible
							title="Edit Walkthrough Scores"
							width={720}
							onCancel={this.hideHeatMapRangesModal}
							closable={!this.state.savingHeatMapRanges}
							maskClosable={!this.state.savingHeatMapRanges}
							footer={[
								<Button
									key="cancel"
									size="large"
									disabled={this.state.savingHeatMapRanges}
									onClick={this.hideHeatMapRangesModal}
								>
									Cancel
								</Button>,
								<OWButton
									key="save"
									type="primary"
									size="large"
									style={{ marginLeft: '16px' }}
									loading={this.state.savingHeatMapRanges}
									onClick={this.onSaveHeatMapRanges}
								>
									Save
								</OWButton>,
							]}
						>
							<WalkthroughHeatMapRangesField
								value={this.getHeatMapRanges()}
								onChange={this.onChangeHeatMapRanges}
								disabled={this.state.savingHeatMapRanges}
								description="Can be overridden per site survey"
							/>
						</Modal>
					) : null}
					<Col span={24} className="rowSpacing">
						<Card
							title="Tasks"
							extra={
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES}>
									<div>
										<Button onClick={this.showEditTaskList}>
											<EditFilled /> Edit Task List
										</Button>
										{this.state.editTaskListVisible ? (
											<OWImporter
												dataLoadComplete={this.state.editTaskListVisible}
												type="site_survey_template"
												entityName="task"
												fields={IMPORT_FIELDS}
												data={this.getSourceDataForSiteSurveyTemplate(siteSurveyTemplate)}
												onComplete={this._onEditTaskListComplete}
												onDataLoad={this.onDataLoad}
												recordHook={this.recordHook}
												validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
											/>
										) : null}
										<OWImportButton
											type="site_survey_template_upload"
											entityName="task"
											fields={IMPORT_FIELDS}
											onDataLoad={this.onDataLoad}
											recordHook={this.recordHook}
											validateRecordsOnSubmit={validateForDuplicateTitlesUnderSameGroup}
											onDownloadSample={this.onDownloadTaskList}
											sampleModalTitle="Replace Task List"
											sampleModalText="You can choose to download the existing list, add new tasks and upload! Do you want to download?"
											sampleButtonText="Download task list"
											showWarningMessage
											renderElement={(openPortal) => (
												<Button type="primary" style={{ marginLeft: '24px' }} onClick={openPortal}>
													<UploadOutlined />
													Replace Task List
												</Button>
											)}
										/>
									</div>
								</AccessPermissionChecker>
							}
						>
							<TaskListRO tasks={nullSafeGetOrElse('taskList.tasks', siteSurveyTemplate, [])} />
						</Card>
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	onSuccess: ownProps.onSuccess,
	siteSurveyTemplatesFetching: state.site_survey_templates.fetching,
	siteSurveyTemplate: state.site_survey_templates.detail,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	isLoading: state.site_survey_templates.updating || state.site_survey_templates.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSiteSurveyTemplate: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.readOne(id)
				: siteSurveyTemplatesRestCrudThunksForBuyer.readOne(id)
		),
	updateTaskList: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? taskListsRestCrudThunksForSupplier.update(entity)
				: taskListsRestCrudThunksForBuyer.update(entity)
		),
	updateSiteSurveyTemplate: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.update(entity)
				: siteSurveyTemplatesRestCrudThunksForBuyer.update(entity)
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(SiteSurveyTemplatesDetailDetailsPage as any)
	) as any
);
